import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { StaleWriteError, isStaleWriteError, isOptlk } from '@/lib/optimistic'
import { ValidationError } from '@/lib/errorReporter'
import { withTimeout } from '@/lib/saveTimeout'
import { statusTone } from '@/lib/statusTone'
import type { Delivery, DeliveryStatus } from '@/types/database'

// Log a status change to status_history. A status that moved with no history
// row has no trail: say so, never drop it.
export async function logStatusChange(
  entityId: string, fromStatus: string, toStatus: string, userId: string, reason?: string,
) {
  const { error } = await supabase.from('status_history').insert({
    entity_type: 'delivery', entity_id: entityId,
    from_status: fromStatus, to_status: toStatus,
    changed_by: userId, reason: reason ?? null,
  })
  if (error) {
    throw new ValidationError(
      'The status changed, but its history row did not save. Tell a manager so the trail can be fixed.')
  }
}

/** What a forward tap needs from a delivery: Yard rows and board rows both fit. */
type TransitionTarget = Pick<Delivery, 'delivery_id' | 'status' | 'intake_path'> & { version?: number }

const wordsOf = (s: string): string => statusTone('delivery', s).label

/**
 * One server call writes the status AND its history row in one transaction and
 * stamps who tapped (set_delivery_status, mig 20261149000000). A stale-version
 * refusal re-reads the row.
 */
async function callSetStatus(delivery: TransitionTarget, toStatus: DeliveryStatus, version: number, reason: string): Promise<void> {
  const { error } = await withTimeout(supabase.rpc('set_delivery_status', {
    p_delivery_id: delivery.delivery_id, p_to: toStatus, p_expected_version: version, p_reason: reason,
  }), 'Delivery status')
  if (!error) return
  if (isOptlk(error)) throw new StaleWriteError('deliveries', delivery.delivery_id, version)
  throw error
}

async function writeStatus(delivery: TransitionTarget, toStatus: DeliveryStatus, reason: string): Promise<void> {
  try {
    await callSetStatus(delivery, toStatus, delivery.version ?? 1, reason)
    return
  } catch (e: unknown) {
    if (!isStaleWriteError(e)) throw e
  }
  const { data: live, error: liveErr } = await withTimeout(supabase.from('deliveries')
    .select('status, version').eq('delivery_id', delivery.delivery_id).maybeSingle(), 'Delivery status re-read')
  if (liveErr) throw liveErr
  const l = live as { status: string; version: number } | null
  if (!l) throw new ValidationError('Delivery was just updated by someone else. Refresh and try again.')
  if (l.status !== delivery.status) {
    throw new ValidationError(`Not moved: this delivery is now ${wordsOf(l.status)}. Check it and tap again if needed.`)
  }
  // Another write bumped the version; the status is still ours to move.
  await callSetStatus(delivery, toStatus, l.version, reason)
}

/**
 * The forward status write behind DeliveryActionButtons (F01 UF-01), shared by
 * the board card, Table view, delivery page and Yard card. One hook, one write.
 * A stale-version refusal re-reads the row: if its status is still the one on
 * screen the tap is applied once more with the fresh version; if someone moved
 * it, the tap says where it is now.
 */
export function useDeliveryTransition() {
  const qc = useQueryClient()
  const m = useMutation({
    mutationFn: async ({ delivery, toStatus, reason }: {
      delivery: TransitionTarget; toStatus: DeliveryStatus; reason: string
    }) => {
      await writeStatus(delivery, toStatus, reason)
    },
    onSettled: (_d, _e, v) => {
      // On failure too: another tap may have moved the row.
      void qc.invalidateQueries({ queryKey: ['deliveries'] })
      void qc.invalidateQueries({ queryKey: ['yard-deliveries'] })
      void qc.invalidateQueries({ queryKey: ['delivery', v.delivery.delivery_id] })
      void qc.invalidateQueries({ queryKey: ['delivery-history', v.delivery.delivery_id] })
      void qc.invalidateQueries({ queryKey: ['delivery-last-tap', v.delivery.delivery_id] })
    },
  })
  const forId = m.variables?.delivery.delivery_id ?? null

  /** Props for DeliveryActionButtons: only the delivery that was tapped shows state. */
  const propsFor = useCallback((d: { delivery_id: string }) => {
    const mine = forId === d.delivery_id
    return {
      busy: m.isPending,
      saving: mine && m.isPending,
      error: mine && m.isError ? friendly(m.error) : null,
      done: mine && m.isSuccess && m.variables ? wordsOf(m.variables.toStatus) : null,
    }
  }, [forId, m.isPending, m.isError, m.isSuccess, m.error, m.variables])

  return {
    mutate: (delivery: TransitionTarget, toStatus: DeliveryStatus, reason: string) =>
      m.mutate({ delivery, toStatus, reason }),
    propsFor,
  }
}

const friendly = (e: unknown): string => {
  const m = (e as { message?: unknown } | null)?.message   // a PostgREST error is not an Error instance
  return typeof m === 'string' && m ? m : 'The status did not change. Try again.'
}
